import { msg } from "@lingui/core/macro";
import { type Component, type ComponentProps, For, Show, Suspense } from "solid-js";
import {
  cn,
  type getMyDayTodosCountQuery,
  type getTodosCountQuery,
  type ListWithMeta,
} from "@listto/shared";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { TaskCountBadge } from "./task-count-badge";
import { CheckSquare, FolderInput, List as ListIcon, Star, Trash2 } from "@fleet/icons";
import { useI18n } from "@/providers/i18n-provider";
import type { PowerSyncDrizzleDB } from "@listto/powersync";

interface TodoListStickyHeaderProps {
  isSticky: boolean;
  title: string;
  icon: Component<ComponentProps<"svg">>;
  /** Emoji icon from a real list, if any */
  listIcon: string | null | undefined;
  isSelectionMode: boolean;
  selectedCount: number;
  hasItems: boolean;
  lists: ListWithMeta[] | undefined;
  listId: string;
  userId: string | undefined;
  countQueryFn: (
    db: PowerSyncDrizzleDB,
    userId: string,
  ) => ReturnType<typeof getTodosCountQuery> | ReturnType<typeof getMyDayTodosCountQuery>;
  onToggleSelection: () => void;
  onBulkMove: (targetListId: string) => void;
  onBulkImportant: () => void;
  onBulkDelete: () => void;
}

export function TodoListStickyHeader(props: TodoListStickyHeaderProps) {
  const { t } = useI18n();

  return (
    <header
      class={cn(
        "absolute top-0 left-0 right-0 h-14 border-b border-border bg-background/95 backdrop-blur-sm z-30 flex items-center justify-between px-6 transition-all duration-300 ease-in-out",
        props.isSticky
          ? "translate-y-0 opacity-100 shadow-sm"
          : "-translate-y-full opacity-0 pointer-events-none",
      )}
    >
      {/* Left: icon + title */}
      <div class="flex items-center gap-2.5 min-w-0 mr-4 flex-1 transition-all duration-300 ease-in-out">
        <div class="flex items-center gap-2 min-w-0">
          <Show
            when={props.listIcon}
            fallback={<props.icon class="h-4.5 w-4.5 text-primary shrink-0" />}
          >
            {(iconEmoji) => (
              <span class="text-base shrink-0 leading-none select-none">{iconEmoji()}</span>
            )}
          </Show>
          <span class="text-sm font-semibold truncate text-foreground select-none">
            {props.title}
          </span>
        </div>
      </div>

      {/* Right: selection actions + count badge */}
      <div class="flex items-center gap-4 shrink-0 transition-all duration-300 ease-in-out origin-right">
        <Show when={props.isSelectionMode && props.selectedCount > 0}>
          <div class="flex items-center gap-2 animate-in fade-in slide-in-from-right-4">
            <span class="text-xs font-bold text-primary mr-2">
              {props.selectedCount} {t(msg`selected`)}
            </span>

            <Popover>
              <PopoverTrigger>
                <Button variant="outline" size="sm" class="h-8 gap-2">
                  <FolderInput class="h-3.5 w-3.5" />
                  {t(msg`Move`)}
                </Button>
              </PopoverTrigger>
              <PopoverContent class="w-56 p-2">
                <div class="space-y-1">
                  <p class="text-[10px] font-bold text-muted-foreground uppercase px-2 py-1">
                    {t(msg`Move to...`)}
                  </p>
                  <For each={props.lists}>
                    {(list) => (
                      <Button
                        variant="ghost"
                        size="sm"
                        class="w-full justify-start text-xs"
                        onClick={() => {
                          props.onBulkMove(list.id);
                        }}
                      >
                        <ListIcon class="h-3 w-3 mr-2" />
                        {list.name}
                      </Button>
                    )}
                  </For>
                </div>
              </PopoverContent>
            </Popover>

            <Button variant="outline" size="sm" class="h-8 gap-2" onClick={props.onBulkImportant}>
              <Star class="h-3.5 w-3.5" />
              {t(msg`Important`)}
            </Button>

            <ConfirmDialog
              title={t(msg`Delete tasks`)}
              description={(() => {
                const count = props.selectedCount;
                return t(
                  msg`Are you sure you want to delete ${count} tasks? This action cannot be undone.`,
                );
              })()}
              confirmLabel={t(msg`Delete`)}
              variant="destructive"
              onConfirm={props.onBulkDelete}
              renderTrigger={(open: () => void) => (
                <Button variant="destructive" size="sm" class="h-8 gap-2" onClick={open}>
                  <Trash2 class="h-3.5 w-3.5" />
                  {t(msg`Delete`)}
                </Button>
              )}
            />
          </div>
        </Show>

        <Show when={props.hasItems}>
          <Button
            variant="ghost"
            size="sm"
            onClick={props.onToggleSelection}
            class={cn(
              "text-xs gap-2 whitespace-nowrap shrink-0",
              props.isSelectionMode && "text-primary bg-primary/10",
            )}
          >
            <CheckSquare class="h-4 w-4" />
            {props.isSelectionMode ? t(msg`Cancel`) : t(msg`Select`)}
          </Button>
        </Show>

        <div class="text-xs font-semibold text-foreground/70 bg-muted/40 px-2.5 py-0.5 rounded-full border border-border/20 whitespace-nowrap shrink-0">
          <Suspense fallback={<span>0 {t(msg`tasks`)}</span>}>
            <TaskCountBadge
              listId={props.listId}
              userId={props.userId}
              countQueryFn={props.countQueryFn}
            />
          </Suspense>
        </div>
      </div>
    </header>
  );
}
